/*
 * WebCRD
 * Web to print solution that automates ordering, fulfillment, job ticketing, production management and chargebacks across corporate print centers.
 * Copyright 1999-2025 Rochester Software Associates (service@rocsoft.com)
 */

import { useEffect, useState } from 'react';

import { getLogger } from '~utils/logging';

const logger = getLogger(Symbol('API:PeriodicPoller'));

/**
 * Creates a helper that periodically polls for updates to a value (as long as at least one place needs the value).
 *
 * If you have a single component needing a value, and will never include it twice on the same page, periodicPoller
 * is a little overkill... but the added overhead is probably not sufficient enough to matter.
 *
 * If you have two (or more) components needing the same value, periodicPoller lets them share the results of the
 * request instead of making the request once each.
 * Ex: The Approval Toolbar Entry needs the current count of Orders needing Approval. We include the Approval Toolbar Entry
 *     on the page twice: once in the main Toolbar, and once in the Mobile menu. If did the polling inside the component itself,
 *     then we'd be making two requests every 30 seconds, instead of just one.
 *
 * When using this in a React compoment, use `usePeriodicPoller`.
 * When using this outside of React components, use `listen` and `unlisten`.
 *
 * @param {object} args
 * @param {number} args.intervalMS - The number of milliseconds to wait in between polls
 * @param {Function} args.poll - The function to execute that will return the current value
 * @param {number} args.initialValue - The value that should be used before the first `poll` completes
 */
export default ({ intervalMS, poll, initialValue }) => {
    let lastPollValue = initialValue;
    const listeners = new Set();

    let lastTimeout;
    let polling = false;
    const executePoll = async (executeOnce) => {
        let pollValue = initialValue;
        try {
            pollValue = await poll();
        } catch (e) {
            // The user most likely logged out or otherwise cannot connect to the server (which we should be handling elsewhere)
            // Log about it on the off chance this is limited to just this specific poll
            logger.error('Error executing a periodic poll', e);
        }
        if (polling) {
            // Save the most recent value, so any future places that listen to this poller get the most recent value immediately,
            // instead of needing to wait until the next time it polls.
            lastPollValue = pollValue;
            for (const listener of listeners) {
                listener(pollValue);
            }

            if (!executeOnce) {
                lastTimeout = setTimeout(executePoll, intervalMS);
            }
        }
    };

    const executePollOnce = () => {
        executePoll(true);
    };

    const listen = (listener) => {
        logger.debug('Listener added to periodic poller');
        listeners.add(listener);

        // Update the listener with the current value immediately (some other caller may have already polled at least once)
        listener(lastPollValue);

        if (!polling) {
            // Nobody else is currently polling this value, so start polling now
            polling = true;
            executePoll(false);
        }
    };

    const unlisten = (listener) => {
        logger.debug('Listener removed from periodic poller');
        try {
            listeners && listeners.remove && listeners.remove(listener);
        } catch (e) {
            logger.error('Couldn\'t remove listener from periodic poller', e);
        }

        if (listeners.size === 0) {
            // The caller was the last  listener, so there's no reason to continue polling
            clearTimeout(lastTimeout);
            polling = false;
        }
    };

    const usePeriodicPoller = (opt_shouldListen) => {
        // Start with whatever the last fetched value was
        // For the first component that uses this poller, it will be the `initialValue`, but if another component
        // has already used this poller before, it will be whatever value that previous component got from polling.
        const [pollValue, setPollValue] = useState(lastPollValue);

        useEffect(() => {
            if (opt_shouldListen === false) {
                // In the (unlikely, but not impossible) chance someone else fetched a new value between first render
                // and `useEffect` triggering, update to the latest value (even though we're not polling for updates)
                // We don't do this outside of `opt_shouldListen` because `listen(setPollValue)` will do this for us.
                setPollValue(lastPollValue);

                // The caller explicitly said "don't bother polling", so don't start listening
                // Since we didn't start listening, there's nothing to clean up when the component is removed
                return undefined;
            }

            // Start listening to the polling
            listen(setPollValue);

            return () => {
                // Either the component isn't being rendered anymore, or `opt_shouldListen` changed, so stop listening
                // (if opt_shouldListen changed, but is still not `false`, we'll re-listen next render)
                unlisten(setPollValue);
            };
        }, [opt_shouldListen, setPollValue]);

        // Return the current pollValue
        return pollValue;
    };

    return {
        listen,
        unlisten,
        usePeriodicPoller,
        executePollOnce,
    };
};
